//////////////////////////////////////////////////////////////////////////////
//
//  Request Interceptor
//
//  작성자: MyungHwan Kim
//  생성일: 2025-10-24
//
//  설명: Axios Request Interceptor
//       - Token 자동 추가
//       - Request 로깅
//       - Request ID 추가 (선택)
//
//////////////////////////////////////////////////////////////////////////////

import { AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import logger from '../utils/logger';
import { getStoredAccessToken, isAccessTokenInvalid } from 'Shared/Utils/CredentialStorage';
import { getIsRefreshing, getRefreshPromise, initiateTokenRefresh } from './tokenRefreshHandler';

/**
 * Request Interceptor 설정
 * @param axiosInstance Axios 인스턴스
 */
export const setupRequestInterceptor = (axiosInstance: AxiosInstance) => {
  axiosInstance.interceptors.request.use(
    async (config: InternalAxiosRequestConfig) => {
      // 0. Token Refresh 중이라면 대기 (Request Queueing)
      // 이미 갱신 중인 경우, 새로운 요청을 보내지 않고 대기했다가 갱신된 토큰으로 보냄
      const isRefreshRequest = config.url?.includes('Authentication/RefreshToken');

      if (!isRefreshRequest) {
        // A. 이미 갱신 중인 경우 -> 대기
        if (getIsRefreshing()) {
          const refreshPromise = getRefreshPromise();
          if (refreshPromise) {
            await refreshPromise;
          }
        }
        // B. 유효기간 만료 확인 (Client-Side Check) -> 갱신 시작 및 대기
        // 서버로 401을 받기 전에 미리 갱신하여 불필요한 요청 방지
        // 단, 토큰이 아예 없는 경우(로그아웃 상태)는 갱신 시도하지 않음 + 로그인 페이지 제외
        else if (
          getStoredAccessToken() &&
          isAccessTokenInvalid() &&
          !window.location.pathname.includes('/authentication/sign-in') &&
          !window.location.pathname.includes('/login')
        ) {
          logger.info('⏳ Token expired (client check) - refreshing before request...');
          await initiateTokenRefresh();
        }

        // C. 갱신 후 새 토큰 적용 (또는 기존 토큰)
        const token = getStoredAccessToken();
        if (token && config.headers) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        // If it's not a refresh request, and we've handled token refresh/setting,
        // we can return early to avoid re-processing token logic below.
        // The original code had a `return config;` here, so we'll keep that behavior.
        return config;
      }

      // 1. Token 자동 추가 (외부 서비스가 아닌 경우에만)
      const isExternalService =
        config.url?.includes('mapbox') || config.url?.includes('youtube');

      if (!isExternalService) {
        const token = getStoredAccessToken();
        // 위에서 Refresh 대기 후 이미 설정된 헤더가 없으면 설정
        if (token && config.headers && !config.headers.Authorization) {
          config.headers.Authorization = `Bearer ${token}`;
        }

        // Timezone Header 추가 (서버에서 시간 변환을 위해 사용)
        if (config.headers) {
          try {
            const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
            if (timeZone) {
              config.headers['X-Timezone'] = timeZone;
            }
          } catch (e) {
            // Ignore timezone errors
          }
        }
      }

      // 2. Request 로깅 (개발 환경에서만)
      const isDev = process.env.NODE_ENV === 'development';
      if (isDev) {
        logger.debug(
          `📤 Request: ${config.method?.toUpperCase()} ${config.url}`,
        );
      }

      return config;
    },
    error => {
      logger.error('Request interceptor error', error);
      return Promise.reject(error);
    },
  );
};

export default setupRequestInterceptor;

